import toString from 'lodash/toString';
import get from 'lodash/get';
import createReducer from 'utils/createReducer';
import { sanitizeError } from 'utils/sanitizeError';
import { getFormRendererV3ApiClient } from 'lib/SplashApiClient/subclients/FormRendererV3ApiClient';
import {
    populateFormFields,
    setEventId as setFormRendererEventId,
    setFormData,
    setGuestAccessHash,
    setIsAttending,
    setIsRegistrationOpen,
    setIsUpdating,
    setLimitOneOrderPerEmail,
    setLimitOneTicketTypePerEmail,
    setSubmitUrl,
    setUploadUrl,
    setViewerData,
    setFormSessions,
} from 'common/compounds/FormRenderer/actions';
import { getFormSessionStepsEnabled } from 'common/entities/form/sessionRegistration';
import { setFormInitialValues, setFormValues } from 'common/compounds/FormRenderer/actions/form';
import { getEventId } from 'common/compounds/FormRenderer/selectors';

import {
    NODE_NAME_ADDITIONAL_GUEST_COUNT,
    NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP,
    NODE_NAME_ATTENDING,
} from 'common/entities/formNode/nodeNames';
import { INPUT_TYPE_CHECKBOX } from 'common/entities/formNode/inputTypes';

import { notifyError, setNewNotification } from 'common/compounds/Notification/modules';

import { setColors } from 'common/entities/color';
import { setFonts } from 'common/entities/font';

import SplashApiClient from 'lib/SplashApiClient';
import AttendeeApiClient from 'lib/SplashApiClient/subclients/AttendeeApiClient';
import Logger from 'observability/Logger';

const LOG = new Logger('splashPage');
export const ERROR_LOADING_FORM = 'There was an error loading the form. Please try refreshing the page.';

// Module name
export const REDUCER_NAMESPACE = 'splashPage';
export const UNPUBLISHED_NOTIFICATION = 'UNPUBLISHED_NOTIFICATION';

const PREFIX = 'splash_pages';
const addEffectPrefix = (actionName) => `${PREFIX}/${actionName}`;

// Actions constants
const SET_FORM_ID = addEffectPrefix('SET_FORM_ID');
const SET_EVENT_ID = addEffectPrefix('SET_EVENT_ID');
const SET_LOGGED_IN_USER = addEffectPrefix('SET_LOGGED_IN_USER');
const SET_LOADING = addEffectPrefix('SET_LOADING');
const TOGGLE_OPENED = addEffectPrefix('TOGGLE_OPENED');
export const OPEN_MODAL = addEffectPrefix('OPEN_MODAL');
const CLOSE_MODAL = addEffectPrefix('CLOSE_MODAL');
const SET_EVENT_SUBDOMAIN = addEffectPrefix('SET_EVENT_SUBDOMAIN');
const SET_LOADING_ERROR = addEffectPrefix('SET_LOADING_ERROR');
const SET_MODIFIY_REGISTRATION = addEffectPrefix('SET_MODIFIY_REGISTRATION');
const SET_UPDATING_REGISTRATION_ACTIVE = addEffectPrefix('SET_UPDATING_REGISTRATION_ACTIVE');
const SET_IS_INSIDE_EDITING_IFRAME = addEffectPrefix('SET_IS_INSIDE_EDITING_IFRAME');
export const SET_FORM_STARTED = addEffectPrefix('SET_FORM_STARTED');
const SET_FORM_COMPLETED = addEffectPrefix('SET_FORM_COMPLETED');

// Initial State
const INIT_STATE = {
    open: false,
    loading: false,
    loadingError: false,
    eventSubdomain: '',
    modifyRegistration: false,
    updatingRegistrationActive: false,
    isInsideEditingFrame: false,
    formId: null,
    formStarted: false,
    eventId: null,
    isUserLoggedIn: false,
};

// Reducers
export default createReducer(INIT_STATE, {
    [SET_LOADING](state, { payload }) {
        return {
            ...state,
            loading: payload,
        };
    },

    [SET_FORM_ID](state, { payload }) {
        return {
            ...state,
            formId: payload,
        };
    },

    [SET_EVENT_ID](state, { payload }) {
        return {
            ...state,
            eventId: payload,
        };
    },

    [SET_LOGGED_IN_USER](state, { payload }) {
        return {
            ...state,
            isUserLoggedIn: payload,
        };
    },

    [SET_LOADING_ERROR](state) {
        return {
            ...state,
            loadingError: true,
        };
    },

    [SET_MODIFIY_REGISTRATION](state, { payload }) {
        return {
            ...state,
            modifyRegistration: payload,
        };
    },

    [SET_UPDATING_REGISTRATION_ACTIVE](state, { payload }) {
        return {
            ...state,
            updatingRegistrationActive: payload,
        };
    },

    [SET_EVENT_SUBDOMAIN](state, { payload }) {
        return {
            ...state,
            eventSubdomain: payload,
        };
    },

    [SET_IS_INSIDE_EDITING_IFRAME](state, { payload }) {
        return {
            ...state,
            isInsideEditingFrame: payload,
        };
    },

    [TOGGLE_OPENED](state) {
        return {
            ...state,
            open: !state.open,
        };
    },

    [OPEN_MODAL](state) {
        return {
            ...state,
            open: true,
        };
    },

    [CLOSE_MODAL](state) {
        return {
            ...state,
            open: false,
        };
    },

    [SET_FORM_STARTED](state) {
        return {
            ...state,
            formStarted: true,
        };
    },

    [SET_FORM_COMPLETED](state) {
        return {
            ...state,
            formStarted: false,
        };
    },
});

// Selectors
const getEventSubdomain = (state) => get(state, `${REDUCER_NAMESPACE}.eventSubdomain`);
const getModifyRegistration = (state) => get(state, `${REDUCER_NAMESPACE}.modifyRegistration`);
const getUpdatingRegistrationActive = (state) => get(state, `${REDUCER_NAMESPACE}.updatingRegistrationActive`);
export const getIsInsideEditingIframe = (state) => get(state, `${REDUCER_NAMESPACE}.isInsideEditingFrame`);
export const getSplashPageFormOpen = (state) => get(state, `${REDUCER_NAMESPACE}.open`);
export const getSplashPageFormLoading = (state) => get(state, `${REDUCER_NAMESPACE}.loading`);
export const getFormId = (state) => get(state, `${REDUCER_NAMESPACE}.formId`);
export const getIsUserLoggedIn = (state) => get(state, `${REDUCER_NAMESPACE}.isUserLoggedIn`);
export const getFormStarted = (state) => get(state, `${REDUCER_NAMESPACE}.formStarted`);

// Action creators
export const setLoading = (loading) => ({
    type: SET_LOADING,
    payload: loading,
});

export const setFormId = (formId) => ({
    type: SET_FORM_ID,
    payload: formId,
});

export const setEventId = (eventId) => ({
    type: SET_EVENT_ID,
    payload: eventId,
});

export const setEventSubdomain = (eventSubdomain) => ({
    type: SET_EVENT_SUBDOMAIN,
    payload: eventSubdomain,
});

export const setLoadingError = () => ({
    type: SET_LOADING_ERROR,
});

export const handleOpenModal = () => ({
    type: OPEN_MODAL,
});

export const closeModal = () => ({
    type: CLOSE_MODAL,
});

export const toggleOpened = () => ({
    type: TOGGLE_OPENED,
});

export const setModifyRegistration = (modifyRegistration) => ({
    type: SET_MODIFIY_REGISTRATION,
    payload: modifyRegistration,
});

export const setUpdatingRegistrationActive = (updatingRegistrationActive) => ({
    type: SET_UPDATING_REGISTRATION_ACTIVE,
    payload: updatingRegistrationActive,
});

export const setIsInsideEditingFrame = (isInsideEditingFrame) => ({
    type: SET_IS_INSIDE_EDITING_IFRAME,
    payload: isInsideEditingFrame,
});

export const setIsUserLoggedIn = (loggedInUser) => ({
    type: SET_LOGGED_IN_USER,
    payload: loggedInUser,
});

export const setFormStarted = () => ({
    type: SET_FORM_STARTED,
});

export const setFormCompleted = () => ({
    type: SET_FORM_COMPLETED,
});

export const showUnpublishedNotification = () => setNewNotification('', '', null, UNPUBLISHED_NOTIFICATION);

const shouldFetchSessions = (isFlagEnabled, state, formId) =>
    isFlagEnabled && getFormSessionStepsEnabled(state, formId);

// Thunks
export const populateFormRenderer = (form) => (dispatch) => {
    dispatch(setFormId(toString(form.id)));
    dispatch(setFormData(form));
};

const showError = () => (dispatch) => {
    dispatch(notifyError(ERROR_LOADING_FORM));
};

export const openModal = () => (dispatch, getState) => {
    const state = getState();
    const hasLoadingError = get(state, 'splashPage.loadingError');
    const formOpenAction = hasLoadingError ? showError : handleOpenModal;
    dispatch(formOpenAction());
};

const populateFormSessions = (sessions) => (dispatch) => {
    dispatch(setFormSessions(sessions));
};

export const fetchFormRenderer =
    (isFormSessionRegistrationFlagEnabled, formPreviewId = null) =>
    async (dispatch, getState) => {
        const eventId = getEventId(getState());
        dispatch(setLoading(true));

        try {
            const form = await getFormRendererV3ApiClient().getFormByEventId(eventId, formPreviewId);
            dispatch(populateFormRenderer(form));
            dispatch(populateFormFields(form));

            if (shouldFetchSessions(isFormSessionRegistrationFlagEnabled, getState(), form?.id)) {
                const sessions = await getFormRendererV3ApiClient().getSessions(eventId);
                dispatch(populateFormSessions(sessions));
            }

            dispatch(setLoading(false));
        } catch {
            dispatch(setLoadingError());
            dispatch(setLoading(false));
            dispatch(closeModal());
        }
    };

async function fetchFormSubmission({ clientId, clientSecret, baseUrl, guestAccessHash, eventSubdomain }) {
    const apiClientForAttendee = new SplashApiClient({ baseUrl });
    const attendeeApiClient = new AttendeeApiClient(apiClientForAttendee, guestAccessHash);

    const accessToken = await attendeeApiClient
        .fetchAccessToken(clientId, clientSecret)
        .then(({ access_token: token }) => token);

    return getFormRendererV3ApiClient()
        .getSubmission(eventSubdomain, accessToken)
        .then(({ data }) =>
            data.reduce(
                (acc, next) => ({
                    ...acc,
                    [next.id]: next.value,
                }),
                {},
            ),
        )
        .catch((error) => {
            const sanitizedError = sanitizeError(error);
            LOG.error('Error fetching form submission', sanitizedError);
            throw sanitizedError;
        });
}

export const fetchFilledFormRenderer =
    ({ clientId, clientSecret, baseUrl, guestAccessHash, isFormSessionRegistrationFlagEnabled }) =>
    async (dispatch, getState) => {
        const eventSubdomain = getEventSubdomain(getState());
        const eventId = getEventId(getState());
        dispatch(setLoading(true));

        try {
            const formSubmissionRequest = fetchFormSubmission({
                clientId,
                clientSecret,
                baseUrl,
                guestAccessHash,
                eventSubdomain,
            }).catch((err) => {
                dispatch(showError(err));

                if (err && err.meta && (err.meta.code === 412 || err.meta.code === 500)) {
                    dispatch(setIsUpdating(false));
                    dispatch(closeModal());
                }
            });

            const [form, submission] = await Promise.all([
                getFormRendererV3ApiClient().getFormByEventId(eventId),
                formSubmissionRequest,
            ]);

            const children = get(form, 'root.children');
            const attendingQuestion = children.find((question) => question.name === NODE_NAME_ATTENDING);
            const attendingId = get(attendingQuestion, 'id');
            const isAttending = submission[attendingId];

            if (isAttending === '1') {
                dispatch(setIsAttending());
            }

            dispatch(populateFormRenderer(form));

            if (shouldFetchSessions(isFormSessionRegistrationFlagEnabled, getState(), form?.id)) {
                const sessions = await getFormRendererV3ApiClient().getSessions(eventId);
                dispatch(populateFormSessions(sessions));
            }

            const additionalGuestGroup = children.find(
                (question) => question.name === NODE_NAME_ADDITIONAL_GUEST_TOP_GROUP,
            );
            if (additionalGuestGroup) {
                const additionalGuestQuestion = additionalGuestGroup.children.find(
                    (question) => question.name === NODE_NAME_ADDITIONAL_GUEST_COUNT,
                );
                const additionalGuestId = get(additionalGuestQuestion, 'id');
                const additionalGuestInputType = get(additionalGuestQuestion, 'input_type');

                const submissionParsed = {
                    ...submission,
                    [additionalGuestId]:
                        additionalGuestInputType === INPUT_TYPE_CHECKBOX
                            ? submission[additionalGuestId] === 1
                            : submission[additionalGuestId],
                };

                dispatch(setFormValues(submissionParsed));
                dispatch(setFormInitialValues(submissionParsed));
            } else {
                dispatch(setFormValues(submission));
                dispatch(setFormInitialValues(submission));
            }
        } catch (err) {
            dispatch(showError(err));
            dispatch(setLoadingError());
            dispatch(setIsUpdating(false));
            dispatch(closeModal());
        }

        dispatch(setLoading(false));
    };

const createRegistrationAction = (actionCreator) => (payload) => (dispatch, getState) => {
    dispatch(actionCreator(payload));

    const updatingRegistrationActive = getUpdatingRegistrationActive(getState());
    const modifyRegistration = getModifyRegistration(getState());

    if (modifyRegistration && !updatingRegistrationActive) {
        dispatch(notifyError('Updating responses isn’t active anymore'));
    }
};

export const createUpdatingRegistrationActive = createRegistrationAction(setUpdatingRegistrationActive);
export const createModifyRegistration = createRegistrationAction(setModifyRegistration);

export const loadBootstrapData =
    ({
        uploadUrl,
        submitUrl,
        eventSubdomain,
        fonts,
        colors,
        eventId,
        guestAccessHash,
        isAutoMountingReactApp,
        viewerData,
        modifyRegistration,
        updatingRegistrationActive,
        isInsideEditingFrame,
        limitOneOrderPerEmail,
        limitOneTicketTypePerEmail,
        isUserLoggedIn,
        isRegistrationOpen,
    }) =>
    (dispatch) => {
        dispatch(setUploadUrl(uploadUrl));
        dispatch(setSubmitUrl(submitUrl));
        dispatch(setEventSubdomain(eventSubdomain));
        dispatch(setFonts(fonts));
        dispatch(setColors(colors));
        dispatch(setFormRendererEventId(eventId));
        dispatch(setEventId(eventId));
        dispatch(setGuestAccessHash(guestAccessHash));
        dispatch(setIsUpdating(isAutoMountingReactApp));
        dispatch(setIsInsideEditingFrame(isInsideEditingFrame));
        dispatch(setViewerData(viewerData));
        dispatch(createUpdatingRegistrationActive(updatingRegistrationActive));
        dispatch(createModifyRegistration(modifyRegistration));
        dispatch(setLimitOneOrderPerEmail(limitOneOrderPerEmail));
        dispatch(setLimitOneTicketTypePerEmail(limitOneTicketTypePerEmail));
        dispatch(setIsUserLoggedIn(isUserLoggedIn));
        dispatch(setIsRegistrationOpen(isRegistrationOpen));
    };
